import {
  AppointmentDto,
  AppointmentSlotDto,
  AppointmentSlotStatus,
  AppointmentStatus,
  AppointmentType,
  CalendarAttendee,
  CalendarEvent,
  CalendarEventCategory,
  CalendarEventContextItem,
  CalendarEventContextItemAttribute,
  CalendarEventStatus,
  CalendarLocation,
} from '@workspace/data-types';

/**
 * Règle de sécurité ETXE :
 * Pour un agent immobilier, l'adresse exacte d'un bien en visite n'est dévoilée
 * que 24 heures avant le rendez-vous, et SEULEMENT si la visite est confirmée.
 * Pour le propriétaire, l'adresse de son bien est TOUJOURS visible.
 */
export function isAppointmentAddressRevealed(
  appointment: AppointmentDto,
  slot?: AppointmentSlotDto,
  isOwner = false,
  now = new Date(),
): boolean {
  if (isOwner) {
    return true;
  }

  const statusStr = String(appointment.status || '').toLowerCase();
  const isConfirmed = statusStr === 'confirmed' || appointment.status === AppointmentStatus.CONFIRMED;
  if (!isConfirmed) {
    return false;
  }

  const activeSlot = slot || appointment.confirmedSlot;
  if (!activeSlot?.startAt) {
    return false;
  }

  const startTime = new Date(activeSlot.startAt).getTime();
  if (Number.isNaN(startTime)) {
    return false;
  }

  const twentyFourHoursBefore = startTime - 24 * 60 * 60 * 1000;
  return now.getTime() >= twentyFourHoursBefore;
}

function formatPropertyTypeLabel(type?: string): string | undefined {
  if (!type) return undefined;
  switch (type.toLowerCase()) {
    case 'house':
      return 'Maison';
    case 'apartment':
      return 'Appartement';
    case 'building':
      return 'Immeuble';
    case 'land':
      return 'Terrain';
    case 'commercial':
      return 'Local commercial';
    case 'parking':
      return 'Parking';
    case 'office':
      return 'Bureau';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
}

function buildEventContexts(appointment: AppointmentDto): CalendarEventContextItem[] {
  const contexts: CalendarEventContextItem[] = [];

  if (appointment.context?.property) {
    const propId = String(appointment.context.property);
    const attributes: CalendarEventContextItemAttribute[] = [];

    if (appointment.context.propertySurfaceHabitable) {
      attributes.push({
        label: 'Surface',
        value: `${appointment.context.propertySurfaceHabitable} m²`,
        icon: 'square_foot',
      });
    }

    if (appointment.context.propertyRoomsCount) {
      attributes.push({
        label: 'Pièces',
        value: `${appointment.context.propertyRoomsCount} p.`,
        icon: 'meeting_room',
      });
    }

    if (appointment.context.propertyPriceCents) {
      const priceFormatted = (appointment.context.propertyPriceCents / 100).toLocaleString('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      });
      attributes.push({
        label: 'Prix',
        value: priceFormatted,
        icon: 'euro',
      });
    }

    if (appointment.context.propertyCity) {
      attributes.push({
        label: 'Ville',
        value: appointment.context.propertyCity,
        icon: 'location_on',
      });
    }

    const typeLabel = formatPropertyTypeLabel(appointment.context.propertyType);

    contexts.push({
      id: propId,
      type: 'property',
      title:
        appointment.context.propertyTitle ||
        (typeLabel && appointment.context.propertyCity
          ? `${typeLabel} à ${appointment.context.propertyCity}`
          : typeLabel || 'Bien immobilier'),
      subtitle: appointment.context.propertyCity ? `Situé à ${appointment.context.propertyCity}` : undefined,
      badge: typeLabel || 'Bien immobilier',
      icon: appointment.context.propertyType === 'house' ? 'home' : 'apartment',
      imageUrl: appointment.context.propertyCoverPhotoUrl,
      route: ['/properties', propId],
      attributes,
    });
  }

  if (appointment.context?.discussion) {
    const discId = String(appointment.context.discussion);
    contexts.push({
      id: discId,
      type: 'discussion',
      title: 'Discussion & messagerie',
      subtitle: 'Accéder aux échanges liés à ce rendez-vous',
      badge: 'Messagerie',
      icon: 'forum',
      route: ['/messages', discId],
    });
  }

  return contexts;
}

function buildAttendees(appointment: AppointmentDto, currentUserId?: string): CalendarAttendee[] {
  const attendees: CalendarAttendee[] = [];
  const isOrganizerMe = currentUserId ? String(appointment.organizer) === String(currentUserId) : false;

  attendees.push({
    name: appointment.organizerName || (isOrganizerMe ? 'Moi (Organisateur)' : 'Organisateur'),
    avatarUrl: appointment.organizerAvatarUrl,
    role: 'organizer',
    status: 'accepted',
  });

  if (appointment.attendees && appointment.attendees.length > 0) {
    for (const att of appointment.attendees) {
      const isMe = currentUserId ? String(att.user) === String(currentUserId) : false;
      attendees.push({
        name: isMe ? 'Moi' : att.name || 'Participant',
        avatarUrl: att.avatarUrl,
        role: att.role,
        status: att.status,
      });
    }
  }

  return attendees;
}

function buildBaseTitle(appointment: AppointmentDto, isRevealed: boolean): string {
  if (appointment.type === AppointmentType.PROPERTY_VISIT) {
    const city = appointment.context?.propertyCity;

    // Si l'adresse n'est pas encore révélée, interdiction formelle de faire fuiter la rue ou le numéro
    if (!isRevealed) {
      return city ? `Visite à ${city}` : 'Visite de bien immobilier';
    }

    if (appointment.context?.propertyTitle) {
      return appointment.context.propertyTitle;
    }
    if (appointment.location?.address) {
      return `Visite : ${appointment.location.address}`;
    }
    return city ? `Visite à ${city}` : 'Visite de bien immobilier';
  }
  return 'Rendez-vous';
}

function buildCalendarLocation(
  appointment: AppointmentDto,
  isRevealed: boolean,
  isOwner = false,
): CalendarLocation | undefined {
  const fullAddress = appointment.location?.address || appointment.context?.propertyAddress;
  const city = appointment.context?.propertyCity;

  if (isRevealed) {
    if (!fullAddress) return undefined;
    return {
      name: appointment.context?.propertyTitle || (city ? `Bien à ${city}` : undefined),
      address: fullAddress,
      type: appointment.location?.type || 'physical',
      isRevealed: true,
    };
  }

  // Adresse masquée (non confirmée ou à plus de 24h)
  const maskedAddress = city ? `${city}` : 'Adresse communiquée 24h avant la visite';
  const lockMessage = isOwner
    ? "L'adresse exacte de votre bien sera automatiquement dévoilée à l'agent 24 h avant le rendez-vous."
    : "L'adresse exacte du bien vous sera dévoilée 24 h avant le rendez-vous.";

  return {
    name: city ? `Bien à ${city}` : 'Bien immobilier',
    address: maskedAddress,
    type: appointment.location?.type || 'physical',
    isRevealed: false,
    lockMessage,
  };
}

function buildSingleEvent(
  appointment: AppointmentDto,
  slot: AppointmentSlotDto,
  options: {
    id: string;
    title: string;
    status: CalendarEventStatus;
    category: CalendarEventCategory;
    isRevealed: boolean;
    isOwner?: boolean;
    slotNote?: string;
    currentUserId?: string;
  },
): CalendarEvent<AppointmentDto> {
  const rawStartAt = slot.startAt as unknown;
  const startAt =
    rawStartAt instanceof Date
      ? rawStartAt.toISOString()
      : typeof rawStartAt === 'string'
        ? rawStartAt
        : new Date().toISOString();

  const rawEndAt = slot.endAt as unknown;
  const endAt =
    rawEndAt instanceof Date ? rawEndAt.toISOString() : typeof rawEndAt === 'string' ? rawEndAt : startAt;

  const descParts: string[] = [];
  if (options.slotNote) {
    descParts.push(options.slotNote);
  }
  if (!options.isRevealed) {
    descParts.push(
      options.isOwner
        ? "Remarque : l'adresse exacte de votre bien sera automatiquement dévoilée à l'agent 24 h avant le rendez-vous."
        : "Remarque : l'adresse exacte du bien vous sera dévoilée 24 h avant le rendez-vous.",
    );
  }
  if (appointment.notes) {
    descParts.push(`Notes : ${appointment.notes}`);
  }
  const description = descParts.length > 0 ? descParts.join('\n\n') : undefined;

  return {
    id: options.id,
    title: options.title,
    description,
    startAt,
    endAt,
    allDay: false,
    status: options.status,
    category: options.category,
    location: buildCalendarLocation(appointment, options.isRevealed, options.isOwner),
    attendees: buildAttendees(appointment, options.currentUserId),
    contexts: buildEventContexts(appointment),
    sourceId: 'etxe-appointments',
    url: appointment.context?.discussion ? `/messages/${appointment.context.discussion}` : undefined,
    syncInfo: {
      uid: `appointment-${options.id}@etxe.immo`,
    },
    metadata: appointment,
  };
}

/**
 * Transforme un AppointmentDto métier ETXE en une liste de CalendarEvent standard.
 *
 * Règles d'affichage & sécurité :
 * - Si le statut est "confirmed" : affiche SEULEMENT le "confirmedSlot".
 * - Si le statut est en "pending" / "proposed" : affiche uniquement les proposedSlots dont le statut est "pending" ou "proposed" (exclut les créneaux déclinés).
 * - Règle des 24h : pour un agent, l'adresse exacte est masquée jusqu'à 24h avant le créneau confirmé.
 */
export function mapAppointmentToCalendarEvents(
  appointment: AppointmentDto,
  currentUserId?: string,
  isOwner = false,
): CalendarEvent<AppointmentDto>[] {
  const apptId = appointment._id
    ? String(appointment._id)
    : (appointment as unknown as { id?: string }).id || '';

  const statusStr = String(appointment.status || '').toLowerCase();

  // 1. Si le status est "confirmed" : affiche SEULEMENT le "confirmedSlot"
  if (statusStr === 'confirmed' || appointment.status === AppointmentStatus.CONFIRMED) {
    const slot =
      appointment.confirmedSlot ||
      appointment.proposedSlots?.find((s) => String(s.status).toLowerCase() === 'accepted');

    if (!slot) {
      return [];
    }

    const isRevealed = isAppointmentAddressRevealed(appointment, slot, isOwner);
    const baseTitle = buildBaseTitle(appointment, isRevealed);

    return [
      buildSingleEvent(appointment, slot, {
        id: apptId,
        title: baseTitle,
        status: 'confirmed',
        category: 'jade',
        isRevealed,
        isOwner,
        currentUserId,
      }),
    ];
  }

  // 2. Si le status est "completed"
  if (statusStr === 'completed' || appointment.status === AppointmentStatus.COMPLETED) {
    const slot =
      appointment.confirmedSlot ||
      appointment.proposedSlots?.find((s) => String(s.status).toLowerCase() === 'accepted') ||
      appointment.proposedSlots?.[0];

    if (!slot) {
      return [];
    }

    // Un événement terminé a eu lieu : l'adresse est révélée
    const isRevealed = true;
    const baseTitle = buildBaseTitle(appointment, isRevealed);

    return [
      buildSingleEvent(appointment, slot, {
        id: apptId,
        title: baseTitle,
        status: 'completed',
        category: 'primary',
        isRevealed,
        isOwner,
        currentUserId,
      }),
    ];
  }

  // 3. Si le status est "cancelled" ou "no_show"
  if (
    statusStr === 'cancelled' ||
    statusStr === 'no_show' ||
    appointment.status === AppointmentStatus.CANCELLED ||
    appointment.status === AppointmentStatus.NO_SHOW
  ) {
    const slot = appointment.confirmedSlot || appointment.proposedSlots?.[0];
    if (!slot) {
      return [];
    }

    const isRevealed = isAppointmentAddressRevealed(appointment, slot, isOwner);
    const baseTitle = buildBaseTitle(appointment, isRevealed);

    return [
      buildSingleEvent(appointment, slot, {
        id: apptId,
        title: `${baseTitle} (Annulé)`,
        status: 'cancelled',
        category: 'ruby',
        isRevealed,
        isOwner,
        currentUserId,
      }),
    ];
  }

  // 4. Si c'est en "pending" / "proposed" / "rescheduled" : affiche les proposedSlot "pending" / "proposed"
  const pendingSlots = (appointment.proposedSlots || []).filter((s) => {
    const sStatus = String(s.status || '').toLowerCase();
    return (
      sStatus === 'proposed' ||
      sStatus === 'pending' ||
      sStatus === AppointmentSlotStatus.PROPOSED ||
      !s.status
    );
  });

  if (pendingSlots.length > 0) {
    const isRescheduled = statusStr === 'rescheduled' || appointment.status === AppointmentStatus.RESCHEDULED;

    return pendingSlots.map((slot, index) => {
      const slotId = slot._id ? String(slot._id) : `${index}`;
      const isRevealed = isAppointmentAddressRevealed(appointment, slot, isOwner);
      const baseTitle = buildBaseTitle(appointment, isRevealed);

      const hasMultiple = pendingSlots.length > 1;
      const optionLabel = hasMultiple ? ` (Option ${index + 1}/${pendingSlots.length})` : ' (En attente)';
      const slotNote = hasMultiple
        ? `Créneau proposé : Option ${index + 1} sur ${pendingSlots.length} (en attente de confirmation)`
        : 'Créneau proposé en attente de confirmation';

      return buildSingleEvent(appointment, slot, {
        id: `${apptId}_slot_${slotId}`,
        title: `${baseTitle}${optionLabel}`,
        status: 'tentative',
        category: isRescheduled ? 'violet' : 'amber',
        isRevealed,
        isOwner,
        slotNote,
        currentUserId,
      });
    });
  }

  // Fallback si aucun créneau pending n'a été trouvé mais qu'un confirmedSlot existe
  if (appointment.confirmedSlot) {
    const isRevealed = isAppointmentAddressRevealed(appointment, appointment.confirmedSlot, isOwner);
    const baseTitle = buildBaseTitle(appointment, isRevealed);

    return [
      buildSingleEvent(appointment, appointment.confirmedSlot, {
        id: apptId,
        title: baseTitle,
        status: 'tentative',
        category: 'amber',
        isRevealed,
        isOwner,
        currentUserId,
      }),
    ];
  }

  return [];
}

/**
 * Fonction de compatibilité retournant le premier événement calculé.
 */
export function mapAppointmentToCalendarEvent(
  appointment: AppointmentDto,
  currentUserId?: string,
  isOwner = false,
): CalendarEvent<AppointmentDto> {
  const events = mapAppointmentToCalendarEvents(appointment, currentUserId, isOwner);
  if (events.length > 0) {
    return events[0];
  }

  // Fallback si aucun événement n'a été généré
  const rawCreatedAt = appointment.createdAt as unknown;
  const createdAtIso =
    rawCreatedAt instanceof Date
      ? rawCreatedAt.toISOString()
      : typeof rawCreatedAt === 'string'
        ? rawCreatedAt
        : new Date().toISOString();

  const apptId = appointment._id
    ? String(appointment._id)
    : (appointment as unknown as { id?: string }).id || '';

  return {
    id: apptId,
    title: buildBaseTitle(appointment, isOwner),
    startAt: createdAtIso,
    endAt: createdAtIso,
    allDay: false,
    status: 'tentative',
    category: 'amber',
    metadata: appointment,
  };
}
